import { useState } from 'react';

import type { HistoryContent } from 'components/publish-history/types';
import { PageError } from 'contexts/alert/AlertContext';
import { formatAlertMessage, useAlert } from 'contexts/alert/useAlert';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { PageDtoPreviewLinkInput, usePageDtoPreviewLinkMutation } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';

const PREVIEW_ERROR_MESSAGE = 'Could not set up previewing for the page.';

export const usePagePreview = () => {
  const { alertError, alertModules } = useAlert();
  const { getDTO, deleteResolvedFieldsFromExternalDTO } = useDataModelContext();
  const { mutateAsync: pagePreviewLinkMutateAsync, isLoading } = usePageDtoPreviewLinkMutation();
  const [previewLink, setPreviewLink] = useState<string | null>(null);
  const currentProperty = useConvertedProperty();

  const handlePreviewPage = async (page?: HistoryContent) => {
    let pageDTO = page ?? getDTO(true);
    pageDTO = deleteResolvedFieldsFromExternalDTO(pageDTO);

    const pagePreviewLinkInput: PageDtoPreviewLinkInput = {
      publicationKey: currentProperty ?? '',
      pageDTO
    };
    try {
      const resp = await pagePreviewLinkMutateAsync({
        pagePreviewLinkInput
      });
      if (resp.pageDTOPreviewLink) {
        // TODO: remove this when UAT for stg test is done
        if (currentProperty === 'wsj' && resp.pageDTOPreviewLink.includes('/test')) {
          setPreviewLink(resp.pageDTOPreviewLink.replace('test', 'world'));
        } else setPreviewLink(resp.pageDTOPreviewLink);
      } else {
        alertError(PREVIEW_ERROR_MESSAGE);
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.message.startsWith('PAGE_VALIDATION_ERROR#')) {
          const errorJson = e.message.replace('PAGE_VALIDATION_ERROR#', '');
          const pageError = JSON.parse(errorJson) as PageError;
          alertError(`${PREVIEW_ERROR_MESSAGE}\n${formatAlertMessage(pageError)}`, { wrapNewlines: true });
          alertModules(pageError.validationError!);
        } else {
          alertError(`${PREVIEW_ERROR_MESSAGE} ${e.message}`);
        }
      } else {
        alertError(PREVIEW_ERROR_MESSAGE);
      }
    }
  };

  return {
    isLoading,
    handlePreviewPage,
    previewLink
  };
};
